import React, { useEffect, useState, useMemo } from "react";
import { useParams, NavLink } from "react-router-dom";
import api from "@/lib/api";
import { RowActions } from "@/components/RowActions";
import { toast } from "sonner";
import { PlusIcon, TrashIcon, PencilIcon, CaretUpDownIcon, CheckIcon, WrenchIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SignatureManager } from "@/components/SignatureManager";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { getUser } from "@/lib/secureStorage";
import CustomerFormDialog from "./masters/CustomerFormDialog";
import SupplierFormDialog from "./masters/SupplierFormDialog";
import SupplierGstClassDialog from "./masters/SupplierGstClassDialog";
import { hasPerm } from "@/lib/auth";
import { displayRegType, canClassifySupplierGst } from "@/lib/supplierGstClass";

const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
const selCls = "bg-[var(--bg-0)] border-[var(--border)] text-white h-9";

// Feb 2026 · FastAPI 422 detail flattener — Pydantic returns `detail`
// as a LIST of `{type, loc, msg, input, url}` objects. Passing that
// list to `toast.error` (or rendering it in JSX) crashes React with
// "Objects are not valid as a React child". This helper coerces any
// FastAPI error payload into a readable single-line string.
function apiErr(e, fallback = "Request failed") {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map(x => {
      if (typeof x === "string") return x;
      const field = Array.isArray(x?.loc) ? x.loc.filter(k => k !== "body").join(".") : "";
      const msg = x?.msg || "invalid";
      return field ? `${field}: ${msg}` : msg;
    }).join(" · ");
  }
  if (typeof d === "object") return d.message || d.msg || JSON.stringify(d);
  return String(d);
}

// Feb 2026 · Light password strength gauge — pure nudge, not a hard policy.
// Backend accepts any bcrypt-able string; this only hints to the admin that
// they've picked a decent password. Score is charset-variety (lowercase +
// uppercase + digit + symbol) capped by length. Pure client-side.
function PasswordStrengthMeter({ value }) {
  const pw = String(value || "");
  const hasLower  = /[a-z]/.test(pw);
  const hasUpper  = /[A-Z]/.test(pw);
  const hasDigit  = /\d/.test(pw);
  const hasSymbol = /[^a-zA-Z0-9]/.test(pw);
  const variety = (hasLower ? 1 : 0) + (hasUpper ? 1 : 0) + (hasDigit ? 1 : 0) + (hasSymbol ? 1 : 0);
  // 0 empty · 1 weak · 2 fair · 3 good · 4 strong
  let score = 0;
  if (pw.length >= 4)  score = Math.max(score, 1);
  if (pw.length >= 6)  score = Math.max(score, Math.min(2, variety));
  if (pw.length >= 8)  score = Math.max(score, Math.min(3, variety));
  if (pw.length >= 12 && variety >= 3) score = 4;
  const label = ["", "Weak", "Fair", "Good", "Strong"][score] || "Weak";
  const tone  = ["zinc", "red", "amber", "sky", "emerald"][score] || "red";
  const pipCls = {
    red:     "bg-red-500",
    amber:   "bg-amber-500",
    sky:     "bg-sky-400",
    emerald: "bg-emerald-500",
    zinc:    "bg-[var(--bg-2)]",
  }[tone];
  const labelCls = {
    red:     "text-red-400",
    amber:   "text-amber-400",
    sky:     "text-sky-400",
    emerald: "text-emerald-400",
    zinc:    "text-zinc-500",
  }[tone];
  const tips = [];
  if (pw.length < 8) tips.push("8+ chars");
  if (!hasUpper) tips.push("UPPER");
  if (!hasLower) tips.push("lower");
  if (!hasDigit) tips.push("0-9");
  if (!hasSymbol) tips.push("symbol");
  return (
    <div className="mt-1.5" data-testid="pw-strength">
      <div className="flex items-center gap-1.5">
        {[1, 2, 3, 4].map(p => (
          <span key={p} className={`h-1 flex-1 rounded-full ${p <= score ? pipCls : "bg-[var(--bg-2)]"}`} />
        ))}
        <span data-testid="pw-strength-label" className={`text-[10px] font-mono uppercase tracking-widest ml-1 ${labelCls}`}>{label}</span>
      </div>
      {tips.length > 0 && (
        <div className="text-[10px] text-zinc-500 mt-1" data-testid="pw-strength-hints">
          Add: <span className="text-zinc-400">{tips.join(" · ")}</span>
        </div>
      )}
    </div>
  );
}


const TABS = [
  { key: "machines", label: "Machines", endpoint: "/machines" },
  { key: "machine-types", label: "Machine Types", endpoint: "/machine-types" },
  { key: "machine-type-options", label: "Machine Category", singular: "Machine Category", endpoint: "/machine-type-options" },
  // Feb 2026 · RM Category consolidated into Item Category (Master Data
  // → Item Category). The rm_category_options collection is preserved
  // read-only for legacy RM code composition; the editable master is
  // now /item-categories which supports every item type (RM/FG/SFG/…).
  // Feb 2026 · RM code composer sub-masters — same shape as RM Category
  // so admins can grow/rename picker options for every RM code segment.
  { key: "denier-count-options", label: "Denier/Count", singular: "Denier/Count", endpoint: "/denier-count-options" },
  { key: "tpm-roto-options", label: "TPM/ROTO", singular: "TPM/ROTO", endpoint: "/tpm-roto-options" },
  // Feb 2026 · Yarn Type consolidated into Item Group (Master Data →
  // Item Group). The yarn_type_options collection is preserved read-
  // only for legacy RM code composition; the editable master is now
  // /item-groups which supports category-parent linking + sort/active.
  // Feb 2026 · Article Types — dynamic short-codes powering the Article
  // Master Type dropdown + backend product-code composer (_type_code).
  { key: "article-types", label: "Product Category", singular: "Product Category", endpoint: "/article-types" },
  { key: "items", label: "Product Master", endpoint: "/items" },
  { key: "customers", label: "Customers", endpoint: "/customers" },
  { key: "suppliers", label: "Suppliers", endpoint: "/suppliers" },
  { key: "operators", label: "Operators", endpoint: "/operators" },
  { key: "shifts", label: "Shifts", endpoint: "/shifts" },
  { key: "departments", label: "Departments", endpoint: "/departments" },
  // Feb 2026 · Godown Master tab · was orphaned (SCHEMA existed but no TABS
  // entry so the sidebar link fell through to the first tab and hid the
  // "New Godown" button). Restored here alongside the other Master Data
  // location-type entries.
  { key: "godowns", label: "Godown Master", singular: "Godown", endpoint: "/godowns" },
  // Phase 2 · Bin Location Master (rack/shelf inside a Godown).
  { key: "bin-locations", label: "Bin Location", singular: "Bin", endpoint: "/bin-locations" },
  { key: "colors", label: "Colour Master", endpoint: "/colors" },
  { key: "downtime-reasons", label: "Downtime Reasons", endpoint: "/downtime-reasons" },
  { key: "defects", label: "Defects", endpoint: "/defects" },
  // Feb 2026 · Routing Stages — powers the BOM `routing` dropdown and,
  // in Phase 2, the auto-emit of parallel/sequential process orders on
  // Work Order approval. Preparatory stages sharing the same
  // `parallel_group` (e.g. "PREP" for Warping + Covering + Spandex
  // Warping) run simultaneously; `depends_on` codes must complete first.
  { key: "routing-stages", label: "Routing Stages", singular: "Routing Stage", endpoint: "/routing-stages" },
  // Users & Roles (Feb 2026) — admin-only; the tab is filtered out of TABS
  // at render time by `visibleTabs` for non-admin sessions so operators don't
  // even see it. Backend enforces `users:manage` on every write.
  // Feb 2026 · Users & Roles surfaced as a dedicated Reports & System
  // sidebar link. Keep the tab out of the Master Data strip so there is
  // one source of truth (the sidebar link). Direct-URL access to
  // /masters/users still works — tab just doesn't show in the strip.
  // Feb 2026 · Users & Roles surfaced as a dedicated Reports & System
  // sidebar link. Keep in TABS with `hidden: true` so `/masters/users`
  // still resolves and shows the Users grid (not Machines). No
  // adminOnly here — server RBAC gates every write anyway.
  { key: "users", label: "Users & Roles", endpoint: "/users", hidden: true },
  // ─── Phase 1 · Inventory & Purchase Foundation Masters (Feb 2026) ───
  // Twelve small masters that back the extended Item Master, PR/PO/GRN
  // workflow and configurable document numbering. All follow the same
  // {code,name,description,is_active} shape so the generic table + form
  // in this page renders them without any per-tab custom code.
  { key: "item-categories",  label: "Item Category",  singular: "Item Category",  endpoint: "/item-categories" },
  { key: "item-groups",      label: "Item Group",     singular: "Item Group",     endpoint: "/item-groups" },
  { key: "brands",           label: "Brand",          singular: "Brand",          endpoint: "/brands" },
  { key: "uom-masters",      label: "UOM Master",     singular: "UOM",            endpoint: "/uom-masters" },
  { key: "hsn-codes",        label: "HSN Code",       singular: "HSN Code",       endpoint: "/hsn-codes" },
  { key: "tax-masters",      label: "Tax Master",     singular: "Tax",            endpoint: "/tax-masters" },
  { key: "currencies",       label: "Currency",       singular: "Currency",       endpoint: "/currencies" },
  { key: "payment-terms",    label: "Payment Terms",  singular: "Payment Term",   endpoint: "/payment-terms" },
  { key: "transporters",     label: "Transporter",    singular: "Transporter",    endpoint: "/transporters" },
  { key: "reorder-policies", label: "Reorder Policy", singular: "Reorder Policy", endpoint: "/reorder-policies" },
  { key: "approval-matrix",  label: "Approval Matrix", singular: "Approval Rule", endpoint: "/approval-matrix" },
  { key: "number-series",    label: "Number Series",  singular: "Number Series",  endpoint: "/number-series" },
];

// Computes Machine Model string from category + heads/width/hooks.
// Jacquard → `10/45/384J`, Plain → `10/45`. Width is rendered without trailing `.0`.
function computeMachineModel(f) {
  const heads = parseInt(f.no_of_heads) || 0;
  const wRaw = parseFloat(f.machine_width) || 0;
  const width = Number.isInteger(wRaw) ? String(wRaw) : String(wRaw);
  // Substring match is case-insensitive so uppercased picker values like
  // "JACQUARD LOOM" (from the global uppercase-input hook) still resolve.
  const isJac = (f.category || "").toLowerCase().includes("jacquard");
  if (isJac) {
    const hooks = parseInt(f.jacquard_hooks) || 0;
    return `${heads}/${width}/${hooks}J`;
  }
  return `${heads}/${width}`;
}

const SCHEMA = {
  "machine-type-options": [
    { k: "code", label: "Code" },
    { k: "name", label: "Machine Category Name" },
  ],
  "rm-category-options": [
    // Kept in SCHEMA for legacy direct-URL access; not surfaced in TABS.
    // Editable master is now /masters/item-categories.
    { k: "code", label: "Short Code" },
    { k: "name", label: "RM Category Name" },
    { k: "description", label: "Description" },
  ],
  "denier-count-options": [
    { k: "code", label: "Short Code", placeholder: "40S / 150D / 20/2" },
    { k: "name", label: "Denier or Count" },
    { k: "description", label: "Description" },
  ],
  "tpm-roto-options": [
    { k: "code", label: "Short Code", placeholder: "500TPM / ROTO" },
    { k: "name", label: "TPM / ROTO Label" },
    { k: "description", label: "Description" },
  ],
  "yarn-type-options": [
    // Kept in SCHEMA for legacy direct-URL access; not surfaced in TABS.
    // Editable master is now /masters/item-groups.
    { k: "code", label: "Short Code", placeholder: "COT / POL / NYL" },
    { k: "name", label: "Yarn Type Name" },
    { k: "description", label: "Description" },
  ],
  godowns: [
    { k: "code", label: "Code", placeholder: "RM-STORE / WEAV-2FLR" },
    { k: "name", label: "Godown Name" },
    { k: "kind", label: "Type", type: "select", options: ["RM", "WIP", "FG", "Other"], default: "RM" },
    // Feb 2026 · Department picker sourced live from the Department Master
    // so godown locations stay in sync with the org chart. Falls back to
    // the standard free-text options if `/departments/` is empty.
    { k: "department", label: "Department", type: "select",
      optionsFrom: "/departments/", optionKey: "name",
      fallbackOptions: ["Stores", "Warping", "Weaving", "Knitting", "Covering", "Finishing", "Checking", "Packing", "Dispatch", "Maintenance", "QA"] },
    { k: "description", label: "Description" },
  ],
  // Phase 2 · Bin Location schema · child rows of a Godown. `godown_id`
  // is a live FK dropdown against `/godowns/`.
  "bin-locations": [
    { k: "code", label: "Bin Code", placeholder: "R-1 / A-3-5" },
    { k: "name", label: "Bin Name" },
    { k: "godown_id", label: "Godown", type: "select",
      optionsFrom: "/godowns/", optionKey: "id", optionLabel: "name" },
    { k: "kind", label: "Kind", type: "select",
      options: ["Rack", "Shelf", "Bin", "Floor", "Tote", "Other"], default: "Rack" },
    { k: "max_capacity", label: "Max Capacity", type: "number", default: 0 },
    { k: "max_uom", label: "Capacity UOM", placeholder: "kg / boxes / cones" },
    { k: "is_active", label: "Active", type: "boolean", default: true },
    { k: "description", label: "Description" },
  ],
  // Feb 2026 · Article Types schema — powers the Article Master Type dropdown.
  // Codes are auto-uppercased server-side; sort_order drives picker order.
  "article-types": [
    { k: "code", label: "Short Code" },
    { k: "name", label: "Category Name" },
    { k: "description", label: "Description" },
    { k: "hs_code", label: "HS Code" },
    { k: "sort_order", label: "Sort Order", type: "number" },
    { k: "is_active", label: "Active", type: "boolean" },
  ],
  "machine-types": [
    // Code is auto-generated by the backend (`PLN-001` / `JAC-001`) — shown read-only in the grid
    // and rendered as an "Auto (PLN-XXX)" placeholder in the create dialog. On edit, the existing
    // code is displayed but stays immutable.
    { k: "code", label: "Code", readOnly: true, autoGen: true },
    { k: "name", label: "Brand Name" },
    { k: "category", label: "Machine Type", type: "select",
      optionsFrom: "/machine-type-options/", optionKey: "name",
      fallbackOptions: ["Plain Machine", "Jacquard Machine", "Jacquard Loom"],
      default: "Plain Machine" },
    { k: "no_of_heads", label: "No. of Heads", type: "number" },
    { k: "machine_width", label: "Machine Width", type: "number" },
    { k: "jacquard_hooks", label: "No. of Jacquard Hooks", type: "number" },
    { k: "machine_model", label: "Machine Model", readOnly: true },
    { k: "description", label: "Description" },
  ],
  machines: [
    { k: "code", label: "Code" },
    // `Name` was removed per user request. Backend still requires `name: str`, so the
    // save handler below mirrors `code` into `name` (mirrors the Items master pattern).
    // Machine Model is the primary lookup into the Machine Types master (values like
    // `10/45/384J` / `10/45`). Selecting a model cascades the Machine Type category
    // (Plain/Jacquard) into `machine_type` and the Brand Name into `brand` — so
    // `Machine Type` is consistent with the Machine Types menu.
    { k: "machine_model", label: "Machine Model", type: "combobox",
      optionsFrom: "/machine-types/", optionKey: "machine_model", optionLabel: "machine_model",
      cascade: { id: "machine_type_id", category: "machine_type", name: "brand", no_of_heads: "heads", machine_width: "max_width_mm", jacquard_hooks: "jacquard_hooks" } },
    { k: "machine_type", label: "Machine Type", readOnly: true },
    { k: "brand", label: "Brand Name", readOnly: true },
    // Department picker sourced live from the Departments master so Masters.jsx
    // never diverges from what's actually in `/departments/`. Falls back to the
    // machine's saved value if the master has been cleared.
    { k: "department", label: "Department", type: "select",
      optionsFrom: "/departments/", optionKey: "name" },
    { k: "status", label: "Status", type: "select", options: ["Running","Idle","Setup","Maintenance","Breakdown","Power Off"], default: "Idle" },
    { k: "heads", label: "No. of Heads", type: "number" },
    { k: "jacquard_hooks", label: "No. of Jacquard Hooks", type: "number" },
    { k: "min_width_mm", label: "Min Width mm", type: "number" },
    { k: "max_width_mm", label: "Max Width mm", type: "number" },
    { k: "standard_rpm", label: "Std RPM", type: "number" },
    { k: "capacity_mtr_hr", label: "Capacity m/hr", type: "number" },
    { k: "efficiency_pct", label: "Std Efficiency %", type: "number", default: 85 },
    { k: "availability_pct", label: "Availability %", type: "number", default: 95 },
    { k: "power_kw", label: "Power (kW)", type: "number" },
    { k: "installed_on", label: "Installed On", type: "date" },
  ],
  items: [
    { k: "description", label: "Product Code", type: "combobox", optionsFrom: "/articles/", optionKey: "product_code", optionLabel: "product_code" },
    { k: "hs_code", label: "HS Code", type: "text" },
    { k: "category", label: "Category", type: "select", options: ["Woven","Knitted"], default: "Woven" },
    // Machine Type — sourced from the "Machine Type Options" master tab.
    // Add/edit values there and they instantly appear in this dropdown.
    // Falls back to the built-in list before any options are seeded.
    { k: "machine_type", label: "Machine Type", type: "select",
      optionsFrom: "/machine-type-options/", optionKey: "name",
      fallbackOptions: ["Needle Loom", "Crochet Loom", "Knitting Machine", "Jacquard Loom", "Plain Loom"] },
    { k: "width_mm", label: "Width mm", type: "number" },
    { k: "construction", label: "Design Hooks" },
    { k: "stretch_pct", label: "Stretch %", type: "number" },
    { k: "recovery_pct", label: "Recovery %", type: "number" },
    { k: "ends", label: "Ends", type: "number" },
    { k: "standard_picks_per_cm", label: "Standard Picks / cm", type: "number" },
    { k: "warp_yarn", label: "Warp Yarn" },
    { k: "weft_yarn", label: "Weft Yarn" },
    { k: "spandex_type", label: "Spandex Type" },
    { k: "standard_speed_rpm", label: "Standard Speed RPM", type: "number" },
    { k: "standard_output_mtr_hr", label: "Standard Output m/hr", type: "number" },
    { k: "standard_efficiency_pct", label: "Standard Efficiency %", type: "number", default: 85 },
    { k: "standard_waste_pct", label: "Standard Waste %", type: "number", default: 3 },
    { k: "standard_setup_min", label: "Standard Setup (min)", type: "number", default: 30 },
    { k: "standard_changeover_min", label: "Standard Changeover (min)", type: "number", default: 45 },
    { k: "standard_operator_count", label: "Standard Operators Req.", type: "number", default: 1 },
    { k: "weight_gm_per_meter", label: "Weight g/m", type: "number" },
  ],
  customers: [
    { k: "code", label: "Code", auto: true, autoFrom: "/next/customer-code", autoKey: "code" },
    { k: "name", label: "Name" },
    { k: "contact_person", label: "Contact" },
    { k: "email", label: "Email" },
    { k: "phone", label: "Phone" },
    { k: "country", label: "Country" },
    { k: "region", label: "Region" },
    // Feb 2026 · Payment Terms + Credit Days flow through to the GST
    // Invoice footer and receivables aging bucket. Grid-visible so
    // finance can spot missing terms at a glance.
    { k: "payment_terms", label: "Payment Terms" },
    { k: "credit_days", label: "Credit Days" },
    // Feb 2026 · `addresses` is a repeatable list managed by CustomerFormDialog.
    // Marked custom so the generic table + form skip rendering it.
    { k: "addresses", label: "Addresses", custom: true, listType: "addresses" },
  ],
  operators: [
    { k: "code", label: "Code" },
    { k: "name", label: "Name" },
    { k: "shift", label: "Shift" },
    { k: "department", label: "Department" },
  ],
  // Supplier Master (Feb 2026) — powers the RM Master supplier dropdown and
  // downstream Purchase Orders. GSTIN + contact captured up-front so POs
  // auto-populate without a second data-entry pass.
  suppliers: [
    { k: "code", label: "Code", auto: true, autoFrom: "/next/supplier-code", autoKey: "code" },
    { k: "name", label: "Supplier Name" },
    { k: "short_code", label: "Short Code", placeholder: "e.g. WAMPUM" },
    { k: "contact_person", label: "Contact Person" },
    { k: "email", label: "Email", type: "email" },
    { k: "phone", label: "Phone" },
    { k: "gstin", label: "GSTIN" },
    // Feb 2026 · Explicit State Code column so the Suppliers grid shows
    // the same tax visibility as the Customer grid.
    { k: "state_code", label: "State Code", placeholder: "27" },
    { k: "city", label: "City" },
    { k: "state", label: "State" },
    { k: "country", label: "Country", default: "India" },
    { k: "address", label: "Address" },
    { k: "payment_terms", label: "Payment Terms", placeholder: "e.g. Net 30" },
    { k: "lead_time_days", label: "Lead Time (days)", type: "number", default: 0 },
    { k: "is_active", label: "Active", type: "boolean", default: true },
    { k: "description", label: "Notes" },
    // Feb 2026 · Multi-address book — surfaced on the grid via a summary
    // chip; the bespoke SupplierFormDialog owns the actual editor.
    { k: "addresses", label: "Addresses", custom: true, listType: "addresses" },
  ],
  // Users & Roles master (Feb 2026 · Admin-only)
  // Password is intentionally optional on edit (empty = keep current); the
  // dedicated rotate flow lives in the same dialog by simply typing a new
  // value. Email is immutable after create — backend PATCH ignores it.
  users: [
    { k: "email", label: "Email", type: "email", immutableOnEdit: true },
    { k: "name", label: "Name" },
    { k: "role", label: "Role", type: "select",
      optionsFrom: "/auth/roles", optionKey: "roles",
      // Aug 31, 2026 · fallback list narrowed to the god-mode role only.
      // The picker fetches every admin-created role via `/auth/roles`;
      // this fallback only fires when the API itself is unreachable.
      fallbackOptions: ["Administrator"] },
    { k: "password", label: "Password", type: "password",
      placeholder_edit: "Leave blank to keep current" },
  ],
  // Colour Master (Feb 2026 · promoted from the generic Named collection).
  // Adds a `hex` swatch so operators pick the exact shade once and every
  // Article dialog reads from here via colour_code → name + hex lookup.
  colors: [
    { k: "code", label: "Colour Code", placeholder: "01" },
    { k: "name", label: "Colour Name", placeholder: "WHITE" },
    { k: "hex", label: "Swatch (hex)", type: "color" },
    { k: "description", label: "Notes" },
  ],
  // Feb 2026 · Routing Stages schema — see TAB comment above. `depends_on`
  // is a comma-separated list of predecessor stage codes rendered as a
  // plain text field so admins can type "WARPING,COVERING,SPANDEX_WARPING"
  // without a bespoke multi-select. `parallel_group` tags stages that run
  // simultaneously (e.g. "PREP" for the Warping + Covering + Spandex
  // Warping trio that converges at Weaving).
  "routing-stages": [
    { k: "code", label: "Code", placeholder: "WARPING" },
    { k: "name", label: "Stage Name" },
    { k: "kind", label: "Kind", type: "select",
      options: ["Preparatory", "Main", "Dyeing", "Finishing", "Packing"], default: "Preparatory" },
    { k: "parallel_group", label: "Parallel Group", placeholder: "PREP (blank = sequential)" },
    { k: "depends_on", label: "Depends On", placeholder: "WARPING,COVERING (comma-separated)" },
    { k: "sequence", label: "Sequence", type: "number", default: 0 },
    { k: "is_active", label: "Active", type: "boolean", default: true },
    { k: "description", label: "Description" },
  ],
  named: [
    { k: "code", label: "Code" },
    { k: "name", label: "Name" },
    { k: "description", label: "Description" },
  ],
  // Defects Master (Feb 2026) — extends the Named grid with an optional
  // Category column. Categories feed the Rejection Entry defect grid's
  // Category filter so admins can group defects by department / stage.
  defects: [
    { k: "code", label: "Code" },
    { k: "name", label: "Defect Name" },
    { k: "category", label: "Category", placeholder: "e.g. Weaving / Finishing" },
    { k: "description", label: "Description" },
  ],
  // ─── Phase 1 · Inventory & Purchase Foundation Masters (Feb 2026) ───
  "item-categories": [
    { k: "code", label: "Short Code", placeholder: "e.g. YARN" },
    { k: "name", label: "Category Name", placeholder: "e.g. Yarn & Filament" },
    { k: "sort_order", label: "Sort Order", type: "number", default: 0 },
    { k: "is_active", label: "Active", type: "boolean", default: true },
    { k: "description", label: "Description" },
  ],
  "item-groups": [
    { k: "code", label: "Short Code", placeholder: "e.g. NYLN" },
    { k: "name", label: "Group Name", placeholder: "e.g. Nylon Yarn" },
    { k: "category_id", label: "Parent Category", type: "select",
      optionsFrom: "/item-categories/", optionKey: "id", optionLabel: "name" },
    { k: "sort_order", label: "Sort Order", type: "number", default: 0 },
    { k: "is_active", label: "Active", type: "boolean", default: true },
    { k: "description", label: "Description" },
  ],
  brands: [
    { k: "code", label: "Short Code", placeholder: "e.g. RELIANCE" },
    { k: "name", label: "Brand Name" },
    { k: "is_active", label: "Active", type: "boolean", default: true },
    { k: "description", label: "Description" },
  ],
  "uom-masters": [
    { k: "code", label: "Code", placeholder: "e.g. KG" },
    { k: "name", label: "Unit Name", placeholder: "e.g. Kilogram" },
    { k: "symbol", label: "Print Symbol", placeholder: "e.g. kg" },
    { k: "is_active", label: "Active", type: "boolean", default: true },
    { k: "description", label: "Description" },
  ],
  "hsn-codes": [
    { k: "code", label: "HSN / SAC Code", placeholder: "e.g. 5407" },
    { k: "name", label: "Short Label", placeholder: "e.g. Woven Synthetic Fabric" },
    { k: "default_tax_rate", label: "Default GST %", type: "number", default: 0 },
    { k: "is_active", label: "Active", type: "boolean", default: true },
    { k: "description", label: "Description" },
  ],
  "tax-masters": [
    { k: "code", label: "Tax Code", placeholder: "e.g. GST-18" },
    { k: "name", label: "Tax Name", placeholder: "e.g. GST 18%" },
    { k: "rate", label: "Rate %", type: "number", default: 0 },
    { k: "tax_type", label: "Type", type: "select",
      options: ["GST", "IGST", "CGST", "SGST", "VAT", "CESS", "Exempt"], default: "GST" },
    { k: "is_active", label: "Active", type: "boolean", default: true },
    { k: "description", label: "Description" },
  ],
  currencies: [
    { k: "code", label: "ISO Code", placeholder: "e.g. USD" },
    { k: "name", label: "Currency Name", placeholder: "e.g. US Dollar" },
    { k: "symbol", label: "Symbol", placeholder: "e.g. $" },
    { k: "is_active", label: "Active", type: "boolean", default: true },
  ],
  "payment-terms": [
    { k: "code", label: "Short Code", placeholder: "e.g. NET-30" },
    { k: "name", label: "Term Name", placeholder: "e.g. Net 30 Days" },
    { k: "days", label: "Days from Invoice", type: "number", default: 0 },
    { k: "is_active", label: "Active", type: "boolean", default: true },
    { k: "description", label: "Description" },
  ],
  transporters: [
    { k: "code", label: "Short Code", placeholder: "e.g. VRL" },
    { k: "name", label: "Transporter Name" },
    { k: "contact_person", label: "Contact Person" },
    { k: "phone", label: "Phone" },
    { k: "gstin", label: "GSTIN" },
    { k: "address", label: "Address" },
    { k: "is_active", label: "Active", type: "boolean", default: true },
  ],
  "reorder-policies": [
    { k: "code", label: "Policy Code", placeholder: "e.g. MINMAX" },
    { k: "name", label: "Policy Name" },
    { k: "method", label: "Method", type: "select",
      options: ["Min-Max", "Reorder Point", "EOQ", "Manual"], default: "Min-Max" },
    { k: "leadtime_days", label: "Lead Time (days)", type: "number", default: 0 },
    { k: "safety_days", label: "Safety Days", type: "number", default: 0 },
    { k: "is_active", label: "Active", type: "boolean", default: true },
    { k: "description", label: "Description" },
  ],
  "approval-matrix": [
    { k: "code", label: "Rule Code", placeholder: "e.g. PO-SUPER-10L" },
    { k: "doc_type", label: "Document Type", type: "select",
      options: ["PR", "PO", "GRN", "Stock Adjustment", "Material Issue", "RFQ", "Debit Note", "Supplier Return"], default: "PO" },
    { k: "min_value", label: "Min Value (₹)", type: "number", default: 0 },
    { k: "max_value", label: "Max Value (₹, 0 = unlimited)", type: "number", default: 0 },
    { k: "approver_role", label: "Approver Role", type: "select",
      optionsFrom: "/auth/roles", optionKey: "roles",
      // Aug 31, 2026 · fallback list narrowed to the god-mode role only.
      fallbackOptions: ["Administrator"] },
    { k: "is_active", label: "Active", type: "boolean", default: true },
    { k: "description", label: "Description" },
  ],
  "number-series": [
    { k: "code", label: "Series Code", placeholder: "e.g. PO" },
    { k: "doc_type", label: "Document Type", placeholder: "PR/PO/GRN/MI/…" },
    { k: "prefix", label: "Prefix", placeholder: "e.g. PO" },
    { k: "pattern", label: "Pattern", placeholder: "{prefix}/{fy}/{seq:04d}",
      default: "{prefix}/{fy}/{seq:04d}" },
    { k: "current_no", label: "Current No.", type: "number", default: 0 },
    { k: "fy_reset", label: "Reset on FY", type: "boolean", default: true },
    { k: "is_active", label: "Active", type: "boolean", default: true },
    { k: "description", label: "Description" },
  ],
};

const schemaFor = (tab) => SCHEMA[tab] || SCHEMA.named;

export default function Masters() {
  const { tab } = useParams();
  // Filter Admin-only tabs based on the signed-in user's role. Non-admins
  // don't even see "Users & Roles" in the navigation; backend also enforces
  // `users:manage` on every write for defence-in-depth.
  const me = getUser();
  const isAdmin = (me?.role === "Administrator");
  // Feb 2026 · `hidden: true` tabs (e.g. Users & Roles) resolve when the
  // URL matches but do NOT render in the tab picker — keeps direct-URL
  // + sidebar-link navigation working without cluttering the strip.
  const allTabs = TABS.filter(t => !t.adminOnly || isAdmin);
  const visibleTabs = allTabs.filter(t => !t.hidden);
  const activeTab = allTabs.find(t => t.key === tab) || visibleTabs[0];
  // Feb 2026 · Master-tab quick search — narrows the tab strip by label /
  // key / singular so leadership can jump to e.g. "Yarn Type" without
  // scanning 15+ tabs. Empty query renders every visible tab.
  const [tabQuery, setTabQuery] = useState("");
  const [rowQuery, setRowQuery] = useState("");
  const filteredTabs = useMemo(() => {
    const needle = tabQuery.trim().toLowerCase();
    if (!needle) return visibleTabs;
    return visibleTabs.filter(t =>
      (t.label || "").toLowerCase().includes(needle)
      || (t.key || "").toLowerCase().includes(needle)
      || (t.singular || "").toLowerCase().includes(needle)
    );
  }, [visibleTabs, tabQuery]);

  // Phase 3J.22A-R2 · The Reset Transactions button + dialog are
  // permanently retired here. Bulk transaction wipes are no longer an
  // operational MES function — posted transactions must be corrected
  // via the applicable document reversal / cancellation workflow.
  // Feb 2026 · Bulk Type Migration UI (Article Types tab only) — one-click
  // scan + fix for articles carrying orphaned type strings or blank HS codes.
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcilePreview, setReconcilePreview] = useState(null);
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const openReconcile = async () => {
    setReconcilePreview(null);
    setReconcileOpen(true);
    try {
      const { data } = await api.get("/article-types/reconcile/preview");
      setReconcilePreview(data);
    } catch (e) {
      toast.error(apiErr(e, "Failed to load preview"));
      setReconcileOpen(false);
    }
  };
  const runReconcile = async () => {
    setReconcileBusy(true);
    try {
      const { data } = await api.post("/article-types/reconcile");
      toast.success(`Fixed ${data.type_fixed} type(s) · Back-filled ${data.hs_filled} HS Code(s)`);
      setReconcileOpen(false);
      await load();
    } catch (e) {
      toast.error(apiErr(e, "Reconcile failed"));
    }
    setReconcileBusy(false);
  };
  // Look up in `allTabs` (includes hidden) so /masters/users still matches
  // the Users tab and renders the correct schema instead of falling back to
  // Machines (the first visible tab).
  const schema = schemaFor(tab);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  // Feb 2026 · Reset the row-search whenever the active tab changes so
  // stale queries from another tab don't accidentally empty the table.
  useEffect(() => { setRowQuery(""); }, [activeTab.key]);

  // Feb 2026 · Filter row list by the active row-search query. Matches
  // every scalar schema value + a handful of common metadata keys so users
  // can find a machine by code / brand, a customer by name / gstin, etc.
  const filteredMasterRows = useMemo(() => {
    const needle = rowQuery.trim().toLowerCase();
    const base = !needle ? rows : rows.filter(r => {
      for (const s of schema) {
        const v = r[s.k];
        if (v == null) continue;
        if (Array.isArray(v)) {
          if (v.some(x => JSON.stringify(x).toLowerCase().includes(needle))) return true;
        } else if (String(v).toLowerCase().includes(needle)) {
          return true;
        }
      }
      return ["id", "code", "name", "email", "phone", "gstin"].some(k => String(r[k] || "").toLowerCase().includes(needle));
    });
    // Feb 2026 · Master Data · Machines tab locks to natural-ascending
    // sort by `code` (L2 → L10 → L201 → NL-005), matching the Gantt
    // board so operators see the same order in both places. Other
    // masters keep their default (Latest-on-Top from the backend).
    if (activeTab && activeTab.key === "machines") {
      return [...base].sort((a, b) =>
        String(a.code || "").localeCompare(String(b.code || ""), undefined, { numeric: true, sensitivity: "base" }));
    }
    return base;
  }, [rows, rowQuery, schema, activeTab]);

  const load = async () => {
    const { data } = await api.get(activeTab.endpoint + "/");
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  // Dynamic option sources for combobox-type AND select-type fields.
  // Combos read the full row list; live-selects dedupe the `optionKey` values
  // so the dropdown mirrors whatever is actually in the source master (e.g.
  // Item.machine_type now surfaces every distinct Machine.machine_type value).
  const [dynOpts, setDynOpts] = useState({});
  useEffect(() => {
    const endpoints = Array.from(new Set(
      schema.filter(s => (s.type === "combobox" || s.type === "select") && s.optionsFrom).map(s => s.optionsFrom)
    ));
    if (endpoints.length === 0) { setDynOpts({}); return; }
    Promise.all(endpoints.map(ep => api.get(ep).then(r => [ep, r.data]).catch(() => [ep, []])))
      .then(pairs => setDynOpts(Object.fromEntries(pairs)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const emptyForm = () => {
    const f = {};
    schema.forEach(s => {
      // Feb 2026 · Boolean fields must default to their `default` (or `true`)
      // so the payload survives Pydantic's `bool` validator — a blank string
      // fails with "Input should be a valid boolean, unable to interpret input".
      if (s.type === "boolean") {
        f[s.k] = s.default ?? true;
      } else {
        f[s.k] = s.default ?? (s.type === "number" ? 0 : "");
      }
    });
    return f;
  };

  // Feb 2026 · Customer Master uses a bespoke dialog (repeatable addresses,
  // region dropdown) instead of the generic schema-driven form.
  const [customerDlgOpen, setCustomerDlgOpen] = useState(false);
  const [customerDlgEditing, setCustomerDlgEditing] = useState(null);
  // Feb 2026 · Supplier Master — bespoke dialog to manage `addresses[]`
  // (Bill From / Ship From) that the generic schema form can't model.
  const [supplierDlgOpen, setSupplierDlgOpen] = useState(false);
  const [supplierDlgEditing, setSupplierDlgEditing] = useState(null);
  const [gstClassSupplier, setGstClassSupplier] = useState(null);
  const canGstClassify = canClassifySupplierGst(me, (p) => hasPerm(null, p));

  const openNew = async () => {
    if (activeTab.key === "customers") {
      setCustomerDlgEditing(null);
      setCustomerDlgOpen(true);
      return;
    }
    if (activeTab.key === "suppliers") {
      setSupplierDlgEditing(null);
      setSupplierDlgOpen(true);
      return;
    }
    setEditing(null);
    const f = emptyForm();
    // Auto-fetch server-generated codes (e.g. Customer Master → CUS-0001).
    // Each schema entry can declare { auto: true, autoFrom: "/next/…", autoKey: "code" }
    // and the value is fetched and dropped into the form before opening.
    const autoFields = schema.filter(s => s.auto && s.autoFrom);
    if (autoFields.length > 0) {
      try {
        const results = await Promise.all(autoFields.map(s => api.get(s.autoFrom).then(r => [s.k, r.data?.[s.autoKey || s.k]])));
        results.forEach(([k, v]) => { if (v) f[k] = v; });
      } catch (err) { console.warn("Failed to fetch auto codes for", activeTab.key, err); }
    }
    setForm(f);
    setOpen(true);
  };
  const openEdit = (row) => {
    if (activeTab.key === "customers") {
      setCustomerDlgEditing(row);
      setCustomerDlgOpen(true);
      return;
    }
    if (activeTab.key === "suppliers") {
      setSupplierDlgEditing(row);
      setSupplierDlgOpen(true);
      return;
    }
    setEditing(row); setForm({ ...emptyForm(), ...row }); setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form };
      schema.forEach(s => {
        if (s.type === "number") payload[s.k] = parseFloat(payload[s.k]) || 0;
        // Feb 2026 · Coerce boolean payloads. `<input type="checkbox">` yields
        // true/false, but a legacy row loaded via openEdit could carry "" or
        // undefined — Pydantic's `bool` validator rejects those with the
        // "unable to interpret input" 422 that used to surface as a runtime
        // React crash before the apiErr flattener.
        if (s.type === "boolean") payload[s.k] = payload[s.k] === true || payload[s.k] === "true";
      });
      // Items tab: `code` is hidden — keep the DB field satisfied by mirroring the Article No.
      // Duplicate Product Code guard: client-side pre-flight so operators see the collision
      // immediately without a server round-trip; backend `_check_item_dup` enforces the same
      // rule as a safety net.
      if (activeTab.key === "items") {
        if (!payload.code || !payload.code.trim()) payload.code = (payload.description || "").trim();
        if (!payload.code) { toast.error("Pick an Article No"); return; }
        const pc = (payload.description || "").trim().toLowerCase();
        const dup = rows.find(r => (r.description || "").trim().toLowerCase() === pc && (!editing || r.id !== editing.id));
        if (dup) { toast.error(`Product Code "${payload.description}" already exists in Product Master`); return; }
      }
      // Machines tab: `name` column was removed — mirror `code` into `name` so the
      // required backend field stays satisfied.
      if (activeTab.key === "machines") {
        if (!payload.name || !String(payload.name).trim()) {
          payload.name = (payload.code || "").trim();
        }
        if (!payload.code) { toast.error("Enter a Machine Code"); return; }
      }
      // Machine Types tab: auto-derive `machine_model` and force hooks=0 on Plain machines.
      if (activeTab.key === "machine-types") {
        const isJac = (payload.category || "").toLowerCase().includes("jacquard");
        if (!isJac) payload.jacquard_hooks = 0;
        payload.machine_model = computeMachineModel(payload);
      }
      if (editing) {
        await api.put(`${activeTab.endpoint}/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(activeTab.endpoint + "/", payload);
        toast.success("Created");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(apiErr(e, "Save failed"));
    }
  };

  const remove = async (id) => {
    // window.confirm avoids the ESLint `no-restricted-globals` snag and
    // makes intent explicit. Wrap the API call so a 4xx/5xx (RBAC 403,
    // FK 400, network flake) surfaces as a toast instead of an unhandled
    // promise rejection that crashes the Master Data page. Feb 2026.
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`${activeTab.endpoint}/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(apiErr(e, "Delete failed"));
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-1">Configuration</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Master Data</h1>
        </div>
        <div className="flex items-center gap-2">
          {activeTab.key === "article-types" && isAdmin && (
            <button
              data-testid="reconcile-article-types-btn"
              onClick={openReconcile}
              className="h-10 px-4 border border-amber-500/40 hover:bg-amber-500/10 text-amber-300 text-sm font-semibold rounded-sm flex items-center gap-2"
              title="Scan every Article for orphaned type strings + missing HS Codes, and back-fill them from the Type master."
            >
              <WrenchIcon size={14} weight="bold" />Reconcile Orphans
            </button>
          )}
          {activeTab.key === "customers" && isAdmin && (
            <button
              data-testid="heal-gstins-btn"
              onClick={async () => {
                if (!window.confirm("Walk every customer and back-fill missing State Code, State, and PAN from clean GSTINs?\n\nExisting non-empty values are preserved (safe / idempotent).")) return;
                try {
                  const { data } = await api.post("/customers/heal-gstin");
                  toast.success(`GSTIN heal complete · ${data.healed} customer(s) updated · ${data.checked} address(es) scanned`);
                  await load();
                } catch (e) { toast.error(e?.response?.data?.detail || "Heal failed"); }
              }}
              className="h-10 px-4 border border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-300 text-sm font-semibold rounded-sm flex items-center gap-2"
              title="Admin · back-fill State Code, State, and PAN on every customer address whose GSTIN is a clean 15-char code."
            >
              Heal GSTINs
            </button>
          )}
          <button data-testid="new-master-btn" onClick={openNew} className="h-10 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
            <PlusIcon size={14} weight="bold" />New {activeTab.singular || activeTab.label.replace(/s$/, "")}
          </button>
        </div>
      </div>

      {/* Feb 2026 · Compact "Master Picker" — replaces the 30-tab strip
          now that the sidebar sub-groups cluster masters properly.
          Renders the active master's label + a searchable jump-picker so
          power-users still switch masters without opening the sidebar. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-[var(--border)] pb-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500">
          <span>Editing</span>
          <span className="px-2 py-1 rounded-sm bg-[var(--accent)]/15 border border-[var(--accent)]/30 text-[var(--accent)] font-mono text-[11px]">
            {activeTab.label}
          </span>
        </div>
        <div className="relative flex-1 max-w-md">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">⌕</span>
          <input
            data-testid="master-tab-search"
            value={tabQuery}
            onChange={e => setTabQuery(e.target.value)}
            placeholder="Jump to another master…"
            className="w-full h-9 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] placeholder:text-zinc-500"
          />
          {tabQuery && filteredTabs.length > 0 && (
            <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-sm border border-[var(--border)] bg-[var(--bg-1)] shadow-2xl">
              {filteredTabs.map(t => (
                <NavLink
                  key={t.key}
                  to={`/masters/${t.key}`}
                  data-testid={`tab-${t.key}`}
                  onClick={() => setTabQuery("")}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-[var(--bg-2)] hover:text-white border-b border-[var(--border)] last:border-b-0"
                >
                  <span className="font-medium">{t.label}</span>
                </NavLink>
              ))}
            </div>
          )}
          {tabQuery && filteredTabs.length === 0 && (
            <div className="absolute z-30 mt-1 w-full px-3 py-2 rounded-sm border border-[var(--border)] bg-[var(--bg-1)] text-xs text-zinc-500 italic">
              No master matches "{tabQuery}"
            </div>
          )}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          {visibleTabs.length} masters · use sidebar to browse
        </div>
      </div>

      <div className="tactical-card overflow-x-auto">
        {/* Feb 2026 · Row-level search — filters the ACTIVE master's records
            by matching any schema value + a couple of common metadata keys.
            Empty query renders every row. */}
        <div className="p-4 pb-3 border-b border-[var(--border)]">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--accent)] text-sm">⌕</span>
            <input
              data-testid="master-row-search"
              value={rowQuery}
              onChange={e => setRowQuery(e.target.value)}
              placeholder={`Search ${activeTab.label} records…`}
              className="w-full h-10 pl-10 pr-24 bg-[var(--bg-0)] border-2 border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] placeholder:text-zinc-500"
            />
            {rowQuery && (
              <button
                data-testid="master-row-search-clear"
                onClick={() => setRowQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-7 px-3 text-[10px] rounded-full border border-[var(--border)] hover:border-[var(--accent)] text-zinc-400 hover:text-white font-mono uppercase tracking-widest"
              >
                Clear ✕
              </button>
            )}
          </div>
          {rowQuery && (
            <div className="mt-1 text-[10px] uppercase tracking-widest text-[var(--accent)] font-mono" data-testid="master-row-search-summary">
              · {filteredMasterRows.length} of {rows.length} {activeTab.label.toLowerCase()} match "{rowQuery}"
            </div>
          )}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              {schema.map(s => <th key={s.k} className="text-left px-5 py-3 font-medium">{s.label}</th>)}
              {activeTab.key === "suppliers" && <th className="text-left px-5 py-3 font-medium" data-testid="supplier-gst-reg-type-header">GST Reg. Type</th>}
              <th className="text-right px-5 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMasterRows.map(r => (
              <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40" data-testid={`master-row-${r.code || r.id}`}>
                {schema.map(s => (
                  <td key={s.k} className={`px-5 py-3 ${s.type === "number" ? "font-mono text-right" : ""} text-zinc-300`}>
                    {s.listType === "addresses" ? (
                      // Feb 2026 · Customer addresses summary column · shows the
                      // primary Bill To city + a count chip so planners can spot
                      // customers with multiple ship sites at a glance.
                      (() => {
                        const addrs = Array.isArray(r.addresses) ? r.addresses : [];
                        if (addrs.length === 0) return <span className="text-zinc-500">—</span>;
                        const pb = addrs.find(a => a.is_primary_bill) || addrs[0];
                        const summary = [pb.city, pb.state].filter(Boolean).join(", ") || (pb.address || "").split("\n")[0] || pb.label;
                        // Feb 2026 · Show GSTIN + State-Code chip so
                        // procurement + tax teams get instant tax-status
                        // verification without opening the dialog.
                        const gstin = (pb.gstin || "").toUpperCase();
                        const sc = pb.state_code || (gstin.length >= 2 ? gstin.slice(0, 2) : "");
                        return (
                          <span className="inline-flex flex-col gap-0.5" data-testid={`customer-addr-summary-${r.code}`}>
                            <span className="inline-flex items-center gap-2">
                              <span className="truncate max-w-[220px] text-zinc-300">{summary}</span>
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-[var(--border)] text-zinc-400">{addrs.length}</span>
                            </span>
                            {(gstin || sc) && (
                              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono">
                                {gstin && <span className="px-1.5 py-0.5 rounded-sm border border-[var(--border)] text-zinc-400" title="Primary Bill From GSTIN">{gstin}</span>}
                                {sc && <span className="px-1.5 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300" title="Primary Bill From State Code">SC {sc}</span>}
                              </span>
                            )}
                          </span>
                        );
                      })()
                    ) : s.type === "color" && r[s.k] ? (
                      <span className="inline-flex items-center gap-2">
                        <span
                          data-testid={`swatch-${r.code}`}
                          className="inline-block w-4 h-4 rounded-sm border border-[var(--border)]"
                          style={{ background: /^#[0-9a-fA-F]{6}$/.test(r[s.k]) ? r[s.k] : "transparent" }}
                          title={r[s.k]}
                        />
                        <span className="font-mono text-[11px]">{r[s.k]}</span>
                      </span>
                    ) : (s.optionsFrom && s.optionKey === "id" && r[s.k]) ? (
                      // Feb 2026 · Foreign-key resolver — when a schema field
                      // stores an `id` (e.g. item_groups.category_id → item_
                      // categories.id), look up the referenced row in the
                      // pre-loaded `dynOpts` cache and display the friendly
                      // label (name / code) instead of the raw UUID.
                      (() => {
                        const ref = (dynOpts[s.optionsFrom] || []).find(x => x.id === r[s.k]);
                        if (!ref) return <span className="font-mono text-[10px] text-zinc-500" title={r[s.k]}>— missing —</span>;
                        const label = ref[s.optionLabel || "name"] || ref.name || ref.code || r[s.k];
                        return (
                          <span className="inline-flex items-center gap-2">
                            {ref.code && ref.code !== label && (
                              <span className="font-mono text-[10px] text-[var(--accent)]">{ref.code}</span>
                            )}
                            <span>{label}</span>
                          </span>
                        );
                      })()
                    ) : (s.type === "boolean") ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${r[s.k] !== false ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-zinc-500/15 text-zinc-500 border-zinc-500/30"}`}>
                        {r[s.k] !== false ? "Active" : "Inactive"}
                      </span>
                    ) : (r[s.k] ?? "—")}
                  </td>
                ))}
                {activeTab.key === "suppliers" && (
                  <td className="px-5 py-3">
                    <span data-testid={`supplier-gst-reg-type-${r.code || r.id}`}
                      className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${displayRegType(r) === "UNVERIFIED" ? "border-amber-500/40 text-amber-300" : "border-emerald-500/40 text-emerald-300"}`}>
                      {displayRegType(r)}
                    </span>
                  </td>
                )}
                <td className="px-5 py-3 text-right">
                  <RowActions
                    testid={`master-actions-${r.code || r.id}`}
                    items={[
                      { label: "Edit", testid: `edit-${r.code || r.id}`, onClick: () => openEdit(r) },
                      ...(activeTab.key === "suppliers" && canGstClassify
                        ? [{ label: "GST Type", testid: `gst-type-${r.code || r.id}`, onClick: () => setGstClassSupplier(r) }] : []),
                      { label: "Delete", tone: "danger", testid: `delete-${r.code || r.id}`, onClick: () => remove(r.id) },
                    ]}
                  />
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={schema.length + (activeTab.key === "suppliers" ? 2 : 1)} className="text-center py-16 text-zinc-500">No records</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Feb 2026 · Customer Master bespoke dialog — handles repeatable
          addresses + region dropdown outside the generic schema flow. */}
      <CustomerFormDialog
        open={customerDlgOpen}
        onOpenChange={setCustomerDlgOpen}
        editing={customerDlgEditing}
        onSaved={load}
      />

      {/* Feb 2026 · Supplier Master bespoke dialog — multi-address editor
          (Bill From / Ship From) mirroring the Customer Master flow. */}
      {canGstClassify && (
        <SupplierGstClassDialog
          supplier={gstClassSupplier}
          open={!!gstClassSupplier}
          onOpenChange={(v) => { if (!v) setGstClassSupplier(null); }}
          onSaved={load}
        />
      )}
      <SupplierFormDialog
        open={supplierDlgOpen}
        onOpenChange={setSupplierDlgOpen}
        editing={supplierDlgEditing}
        onSaved={load}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-4xl max-h-[85vh] overflow-y-auto" description="Add or edit a shop-floor loom, godown, colour, or other master record used across the plant.">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "New"} {activeTab.singular || activeTab.label.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {schema.filter(s => !s.custom).map(s => (
              <div key={s.k} className={s.k === "description" || s.k === "name" ? "md:col-span-3" : ""} data-nouppercase={activeTab.key === "users" ? "tree" : undefined}>
                <div className="overline mb-1.5">{s.label}</div>
                {s.type === "select" ? (() => {
                  // Live-select: when `optionsFrom` is set, hydrate options
                  // from the fetched dynOpts payload, dedupe by `optionKey`,
                  // and fall back to `fallbackOptions` when the source is empty.
                  // Accepts BOTH array-of-objects (Machines, etc.) AND
                  // object-with-array (e.g. /auth/roles → `{roles: [...]}`)
                  // shapes so future master endpoints can pick whichever.
                  //
                  // Feb 2026 · When `optionKey === "id"` we treat this as a
                  // foreign-key picker: build `{value, label}` pairs so the
                  // dropdown displays the friendly label (name / code · name)
                  // while still storing the UUID. Prevents raw UUIDs from
                  // leaking into the picker (e.g. Item Group → Parent Category).
                  let opts = (s.options || []).map(o => ({ value: o, label: o }));
                  if (s.optionsFrom) {
                    const raw = dynOpts[s.optionsFrom];
                    let src = [];
                    if (Array.isArray(raw)) src = raw;
                    else if (raw && s.optionKey && Array.isArray(raw[s.optionKey])) src = raw[s.optionKey];
                    const key = s.optionKey || "name";
                    if (Array.isArray(src) && src.length && typeof src[0] === "object") {
                      const labelKey = s.optionLabel || "name";
                      const seen = new Set();
                      opts = [];
                      for (const r of src) {
                        const v = r?.[key];
                        if (!v || seen.has(v)) continue;
                        if (r.is_active === false) continue; // hide retired
                        seen.add(v);
                        const lbl = r?.[labelKey] || r?.name || r?.code || v;
                        // For FK pickers, show `CODE · Name` when both exist.
                        const display = key === "id" && r.code && r.code !== lbl ? `${r.code} · ${lbl}` : lbl;
                        opts.push({ value: v, label: display });
                      }
                    } else {
                      const distinct = Array.from(new Set((src || []).filter(Boolean)));
                      opts = distinct.length > 0
                        ? distinct.map(v => ({ value: v, label: v }))
                        : (s.fallbackOptions || []).map(v => ({ value: v, label: v }));
                    }
                  }
                  return (
                    <Select value={form[s.k] || ""} onValueChange={v => setForm({...form, [s.k]: v})}>
                      <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                        {opts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  );
                })() : s.type === "combobox" ? (
                  <ComboboxField
                    value={form[s.k] || ""}
                    options={dynOpts[s.optionsFrom] || []}
                    optionKey={s.optionKey || "id"}
                    optionLabel={s.optionLabel || s.optionKey || "name"}
                    usedValues={
                      // Product Master · flag Article Nos already picked by another
                      // Product Master row so operators don't accidentally choose
                      // a duplicate (backend enforces uniqueness anyway).
                      activeTab.key === "items" && s.k === "description"
                        ? new Set(rows.filter(r => !editing || r.id !== editing.id).map(r => (r.description || "").trim()))
                        : null
                    }
                    onChange={v => {
                      const patch = { [s.k]: v };
                      // Cascade: when the selected option maps other fields into the form,
                      // pull them across so the user doesn't have to re-type known specs.
                      if (s.cascade && s.optionsFrom) {
                        const opts = dynOpts[s.optionsFrom] || [];
                        const key = s.optionKey || "id";
                        const opt = opts.find(o => String(o[key]) === String(v));
                        if (opt) {
                          Object.entries(s.cascade).forEach(([src, dst]) => {
                            if (opt[src] !== undefined && opt[src] !== null) patch[dst] = opt[src];
                          });
                        } else {
                          // Cleared → wipe cascaded targets so stale data isn't saved.
                          Object.values(s.cascade).forEach(dst => { patch[dst] = 0; });
                          patch[s.cascade.id || "__none__"] = "";
                        }
                      }
                      setForm({ ...form, ...patch });
                    }}
                    testId={`field-${s.k}`}
                    placeholder={`Select ${s.label}…`}
                  />
                ) : (s.readOnly || s.auto) ? (
                  <input
                    data-testid={`field-${s.k}`}
                    type="text"
                    value={
                      s.k === "machine_model" && activeTab.key === "machine-types"
                        ? computeMachineModel(form)
                        : (s.autoGen && !editing ? "" : (form[s.k] ?? ""))
                    }
                    placeholder={s.autoGen && !editing ? `Auto (${(form.category || "").toLowerCase().includes("jacquard") ? "JAC" : "PLN"}-XXX)` : (s.auto ? "Auto-generated" : "")}
                    readOnly
                    disabled
                    className={`${inp} opacity-75 font-mono cursor-not-allowed`}
                    title={s.auto ? "Customer Code is auto-generated on save" : undefined}
                  />
                ) : s.type === "color" ? (
                  // Colour Master hex-picker — pair a native color input with a
                  // text field so operators can either pick visually or paste a
                  // hex code from the buyer's spec sheet.
                  <div className="flex items-center gap-2">
                    <input
                      data-testid={`field-${s.k}-swatch`}
                      type="color"
                      value={/^#[0-9a-fA-F]{6}$/.test(form[s.k] || "") ? form[s.k] : "#cccccc"}
                      onChange={e => setForm({ ...form, [s.k]: e.target.value.toUpperCase() })}
                      className="h-9 w-12 rounded-sm border border-[var(--border)] bg-[var(--bg-1)] cursor-pointer"
                    />
                    <input
                      data-testid={`field-${s.k}`}
                      type="text"
                      value={form[s.k] ?? ""}
                      onChange={e => setForm({ ...form, [s.k]: e.target.value.toUpperCase() })}
                      placeholder="#FFFFFF"
                      className={`${inp} flex-1 font-mono`}
                    />
                  </div>
                ) : s.type === "boolean" ? (
                  // Feb 2026 · Toggle-style checkbox for boolean fields.
                  // Replaces the fallthrough <input type="text"> that used
                  // to submit "" and 422 the whole save.
                  <label className="flex items-center gap-3 h-9 cursor-pointer select-none" data-testid={`field-${s.k}-toggle`}>
                    <input
                      type="checkbox"
                      data-testid={`field-${s.k}`}
                      checked={form[s.k] === true || form[s.k] === "true"}
                      onChange={e => setForm({ ...form, [s.k]: e.target.checked })}
                      className="h-4 w-4 accent-[var(--accent)] cursor-pointer"
                    />
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${
                      (form[s.k] === true || form[s.k] === "true")
                        ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                        : "border-zinc-500/40 text-zinc-500 bg-zinc-500/10"
                    }`}>
                      {(form[s.k] === true || form[s.k] === "true") ? "Active" : "Inactive"}
                    </span>
                  </label>
                ) : (
                  <input
                    data-testid={`field-${s.k}`}
                    type={
                      s.type === "number" ? "number"
                      : s.type === "date" ? "date"
                      // Feb 2026 · Honour password / email types on the users
                      // tab so browsers mask the password AND the global
                      // uppercase CSS rule (which excludes password/email)
                      // leaves them as typed — matches how ops actually
                      // enter mixed-case creds.
                      : s.type === "password" ? "password"
                      : s.type === "email" ? "email"
                      : "text"
                    }
                    placeholder={s.type === "password" && editing ? (s.placeholder_edit || "") : (s.placeholder || "")}
                    value={form[s.k] ?? ""}
                    disabled={activeTab.key === "machine-types" && s.k === "jacquard_hooks" && !(form.category || "").toLowerCase().includes("jacquard")}
                    onChange={e => setForm({...form, [s.k]: s.type === "number" ? e.target.value : e.target.value})}
                    className={inp}
                  />
                )}
                {s.type === "password" && (form[s.k] || "") && (
                  <PasswordStrengthMeter value={form[s.k]} />
                )}
              </div>
            ))}
          </div>
          {activeTab.key === "users" && editing?.id && (
            <div className="mt-3"><SignatureManager userId={editing.id} /></div>
          )}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button data-testid="save-master" onClick={save} className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-sm font-semibold text-white">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 3J.22A-R2 · Reset Transactions dialog permanently retired.
          Bulk wipe is no longer an operational MES capability — use per-document
          reversal / cancellation workflows instead (see Reversal Audit). */}

      {/* Feb 2026 · Bulk Type Migration dialog (Article Types tab only).
          Fetches /article-types/reconcile/preview on open, shows every orphan
          + suggested fix, and lets the admin apply all via POST
          /article-types/reconcile. Silent no-op when the catalogue is clean. */}
      <Dialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-amber-500/40 text-white max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="reconcile-dialog" description="Preview article-type codes that need remapping and choose which rows to sync.">
          <DialogHeader>
            <DialogTitle className="font-display text-amber-300 flex items-center gap-2">
              <WrenchIcon size={18} weight="bold" />Bulk Type Migration
            </DialogTitle>
          </DialogHeader>
          {!reconcilePreview && <div className="py-8 text-center text-sm text-zinc-500">Scanning articles…</div>}
          {reconcilePreview && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="tactical-card p-3 text-center">
                  <div className="overline mb-0.5">Total Orphans</div>
                  <div className="font-display text-2xl font-black" data-testid="reconcile-orphan-count">{reconcilePreview.orphan_count}</div>
                </div>
                <div className="tactical-card p-3 text-center">
                  <div className="overline mb-0.5">Type Fixes</div>
                  <div className="font-display text-2xl font-black text-amber-300">{reconcilePreview.type_fix_count}</div>
                </div>
                <div className="tactical-card p-3 text-center">
                  <div className="overline mb-0.5">HS Back-fills</div>
                  <div className="font-display text-2xl font-black text-emerald-300">{reconcilePreview.hs_backfill_count}</div>
                </div>
              </div>
              {reconcilePreview.orphan_count === 0 && (
                <div className="p-4 border border-emerald-500/30 rounded-sm bg-emerald-500/5 text-emerald-300 text-sm flex items-center gap-2">
                  <CheckIcon size={16} weight="bold" />Every article is already using a canonical type code with an HS Code assigned. Nothing to do.
                </div>
              )}
              {reconcilePreview.orphan_count > 0 && (
                <div className="tactical-card overflow-x-auto max-h-96">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                        <th className="text-left px-3 py-2 font-medium">Product Code</th>
                        <th className="text-left px-3 py-2 font-medium">Description</th>
                        <th className="text-left px-3 py-2 font-medium">Type · Current → Suggested</th>
                        <th className="text-left px-3 py-2 font-medium">HS · Current → Suggested</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reconcilePreview.orphans.map((o) => (
                        <tr key={o.id} className="border-b border-[var(--border)]" data-testid={`orphan-row-${o.id}`}>
                          <td className="px-3 py-2 font-mono text-white">{o.product_code}</td>
                          <td className="px-3 py-2 text-zinc-400 truncate max-w-xs">{o.description}</td>
                          <td className="px-3 py-2 font-mono">
                            {o.type_fix ? (
                              <>
                                <span className="text-red-400 line-through">{o.current_type || "—"}</span>
                                <span className="text-zinc-500 mx-1">→</span>
                                <span className="text-emerald-300 font-semibold">{o.suggested_type}</span>
                              </>
                            ) : (
                              <span className="text-zinc-500">{o.current_type} · ok</span>
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono">
                            {o.hs_fix ? (
                              <>
                                <span className="text-red-400 italic">{o.current_hs_code || "(blank)"}</span>
                                <span className="text-zinc-500 mx-1">→</span>
                                <span className="text-emerald-300 font-semibold">{o.suggested_hs_code}</span>
                              </>
                            ) : (
                              <span className="text-zinc-500">{o.current_hs_code || "—"}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {reconcilePreview.orphan_count > 500 && (
                <div className="text-[11px] text-amber-400 flex items-center gap-1"><WarningCircleIcon size={12} weight="bold" />Preview capped at 500 rows — the Fix All button still processes every article server-side.</div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setReconcileOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400" disabled={reconcileBusy}>Cancel</button>
            <button
              data-testid="reconcile-run-btn"
              onClick={runReconcile}
              disabled={!reconcilePreview || reconcilePreview.orphan_count === 0 || reconcileBusy}
              className="px-4 py-2 rounded-sm bg-amber-500 hover:bg-amber-400 disabled:bg-amber-900/40 disabled:cursor-not-allowed text-sm font-semibold text-white flex items-center gap-2"
            ><WrenchIcon size={12} weight="bold" />{reconcileBusy ? "Fixing…" : "Fix All"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

// Searchable combobox that pulls its options from a live list. Falls back gracefully when list is empty.
// `usedValues` (optional Set<string>) — options whose value is already in the Set get an
// "Already added" amber badge and are visually dimmed + non-selectable. The currently
// selected value (when editing) is exempt so operators can still see their own row.
function ComboboxField({ value, options, optionKey, optionLabel, onChange, testId, placeholder, usedValues }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const src = options || [];
    if (!q) return src.slice(0, 200);
    return src.filter(o => String(o[optionLabel] || "").toLowerCase().includes(q)).slice(0, 200);
  }, [query, options, optionLabel]);
  const display = value || "";
  const isUsed = (val) => usedValues && usedValues.has(String(val || "").trim());
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm text-left flex items-center justify-between hover:border-[var(--accent)] focus:outline-none focus:border-[var(--accent)]"
        >
          <span className={`truncate ${display ? "font-mono" : "text-zinc-500"}`}>{display || placeholder}</span>
          <CaretUpDownIcon size={12} className="text-zinc-500 flex-shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[480px] max-w-[92vw] bg-[var(--bg-1)] border-[var(--border)]" align="start">
        <Command shouldFilter={false}>
          <CommandInput data-testid={`${testId}-search`} value={query} onValueChange={setQuery} placeholder="Search article number…" />
          <CommandList className="max-h-72">
            <CommandEmpty>No articles found.</CommandEmpty>
            {value && (
              <CommandGroup heading="Current">
                <CommandItem value={`__clear__${value}`} onSelect={() => { onChange(""); setOpen(false); }} className="text-zinc-400 text-xs">
                  Clear selection ({value})
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup heading={`Articles · ${filtered.length}${usedValues ? ` · ${filtered.filter(o => isUsed(o[optionKey])).length} already added` : ""}`}>
              {filtered.map(o => {
                const val = String(o[optionKey] ?? "");
                const label = String(o[optionLabel] ?? val);
                const used = isUsed(val) && val !== value;
                return (
                  <CommandItem
                    key={val || label}
                    value={label}
                    onSelect={() => { if (used) return; onChange(val); setOpen(false); setQuery(""); }}
                    disabled={used}
                    data-testid={`${testId}-opt-${val}`}
                    className={`font-mono text-xs ${used ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <CheckIcon size={12} className={`mr-2 ${value === val ? "opacity-100" : "opacity-0"}`} />
                    <span className="flex-1 truncate">{label}</span>
                    {used && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-500/20 text-amber-400 font-semibold whitespace-nowrap" data-testid={`${testId}-badge-${val}`}>Already added</span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
